'use client';

import * as DialogPrimitive from '@radix-ui/react-dialog';
import { cva, type VariantProps } from 'class-variance-authority';
import { X } from 'lucide-react';
import * as React from 'react';

import { useDismissFromAbove } from '../lib/use-dismiss-from-above';
import { cn } from '../lib/utils';

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay ref={ref} className={cn(`fixed inset-0 z-overlay bg-black/25`, className)} {...props} />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

type DialogPrimitiveType = React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>;

type CustomDialogPrimitiveType = {
  hideCloseButton?: boolean;
  overlayClassName?: string;
} & DialogPrimitiveType;

const DialogContent = React.forwardRef<React.ComponentRef<typeof DialogPrimitive.Content>, CustomDialogPrimitiveType>(
  ({ className, children, hideCloseButton, overlayClassName, onInteractOutside, ...props }, ref) => {
    const [setContent, guardDismiss] = useDismissFromAbove(ref);

    return (
      <DialogPortal>
        <DialogOverlay className={overlayClassName} />
        <DialogPrimitive.Content
          ref={setContent}
          className={cn(
            `fixed left-[50%] top-[50%] z-overlay grid max-h-[calc(100%-2rem)] w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 overflow-y-auto border bg-background p-6 shadow-lg sm:rounded-lg`,
            className,
          )}
          {...props}
          onInteractOutside={guardDismiss(onInteractOutside)}
        >
          {children}
          {!hideCloseButton && (
            <DialogPrimitive.Close className="absolute right-2 top-2 rounded-md p-1 opacity-70 ring-offset-background transition-opacity hover:bg-accent hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </DialogPrimitive.Close>
          )}
        </DialogPrimitive.Content>
      </DialogPortal>
    );
  },
);
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col gap-1 text-center text-lg font-semibold sm:text-left', className)} {...props} />
);
DialogHeader.displayName = 'DialogHeader';

const dialogFooterVariants = cva('flex', {
  variants: {
    layout: {
      /** Stacks the buttons below `sm`. */
      responsive: 'flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2',
      /** Keeps the buttons on one right-aligned row at all widths. */
      row: 'flex-row justify-end gap-2',
    },
  },
  defaultVariants: { layout: 'responsive' },
});

export type DialogFooterProps = React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof dialogFooterVariants>;

const DialogFooter = ({ className, layout, ...props }: DialogFooterProps) => (
  <div className={cn(dialogFooterVariants({ layout }), className)} {...props} />
);
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('text-md font-semibold leading-none tracking-tight text-foreground', className)}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-pretty text-sm text-muted-foreground', className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  dialogFooterVariants,
  DialogTitle,
  DialogDescription,
};
